<template>
  <div>
    <only-site-card :com_data="com_data">
      <template v-slot:item_edit>
        <ValidationObserver ref="observer">
          <v-container fluid>
            <v-row>
              <v-col cols="12" sm="2">
                <span class="font-weight-bold">コメント</span>
                <p class="red--text mini">
                  未入力の場合、コメントタブの「<strong>セラピストコメント</strong>」の内容で更新します。
                </p>
              </v-col>
              <v-col cols="12" sm="10">
                <v-row>
                  <v-col cols="12">
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      :name="items.grl_comment.text"
                      :rules="items.grl_comment.rules"
                    >
                      <v-textarea
                        ref="grl_comment"
                        v-model="cast_prop.grl_comment"
                        name="grl_comment"
                        :error-messages="errors"
                        :success="valid"
                        :label="items.grl_comment.text"
                        :placeholder="`コメント`"
                        rows="6"
                        outlined
                        :counter="items.grl_comment.counter"
                        background-color="white"
                      />
                    </ValidationProvider>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-divider class="mt-4 mb-8"></v-divider>
            <v-row>
              <v-col cols="12" sm="2">
                <span class="font-weight-bold">お店より一言</span>
                <p class="red--text mini">
                  未入力の場合、コメントタブの「<strong>お店コメント</strong>」の内容で更新します。
                </p>
              </v-col>
              <v-col cols="12" sm="10">
                <v-row>
                  <v-col cols="12">
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      :name="items.grl_shopcomment.text"
                      :rules="items.grl_shopcomment.rules"
                    >
                      <v-textarea
                        ref="grl_shopcomment"
                        v-model="cast_prop.grl_shopcomment"
                        name="grl_shopcomment"
                        :error-messages="errors"
                        :success="valid"
                        :label="items.grl_shopcomment.text"
                        :placeholder="`お店より一言`"
                        rows="6"
                        outlined
                        :counter="items.grl_shopcomment.counter"
                        background-color="white"
                      />
                    </ValidationProvider>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-divider class="mt-4 mb-8"></v-divider>
            <!-- プロフィール（gpi_idベース。ラベル・項目数はサイトのプロフィール項目管理から動的取得） -->
            <!-- 取得中 -->
            <div v-if="profileLoadingFlg" class="mb-8 grey--text">
              <v-progress-circular
                indeterminate
                size="20"
                width="2"
                class="mr-2"
              ></v-progress-circular>
              プロフィール項目を取得中...
            </div>
            <!-- 取得失敗 -->
            <div v-else-if="profileError" class="red--text mb-8">
              プロフィール項目の取得に失敗しました。時間をおいて再度お試しください。
            </div>
            <!-- 項目 -->
            <template v-else>
              <div v-for="item in profileItems" :key="item.key">
                <v-row>
                  <v-col cols="12" sm="2"
                    ><span class="font-weight-bold">{{
                      item.text
                    }}</span></v-col
                  >
                  <v-col cols="12" sm="10">
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="cast_prop[item.key]"
                          :name="item.key"
                          :label="item.text"
                          success
                          outlined
                          background-color="white"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-divider class="mt-4 mb-8"></v-divider>
              </div>
            </template>

            <!-- チェックボックス群（可能プレイ/見た目/特徴/性格/プレイスタイル。複数選択） -->
            <div v-for="group in checkboxGroups" :key="group.key">
              <v-row>
                <v-col cols="12" sm="2"
                  ><span class="font-weight-bold">{{
                    group.title
                  }}</span></v-col
                >
                <v-col cols="12" sm="10">
                  <v-row class="mt-2 mb-2">
                    <v-col
                      v-for="opt in group.options"
                      :key="group.key + '_' + opt.value"
                      cols="6"
                      sm="4"
                      md="3"
                    >
                      <v-checkbox
                        class="mt-1"
                        v-model="selected[group.key]"
                        :name="group.key"
                        :value="opt.value"
                        :label="opt.label"
                        hide-details
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-divider class="mt-4 mb-8"></v-divider>
            </div>
          </v-container>
        </ValidationObserver>
      </template>
    </only-site-card>
  </div>
</template>

<script>
import {
  set,
  ref,
  reactive,
  toRefs,
  defineComponent,
} from "@vue/composition-api";
import { repositoryFactory } from "@/repository/repositoryFactory";
import store from "@/store/index.js";
import setting from "@/common/setting.js";
import utilFunc from "@/common/utilFunc.js";
import retryRequest from "@/common/retryRequest";

export default defineComponent({
  props: ["com_data"],
  setup(props, ctx) {
    const CastPropsRepository = repositoryFactory.get("castProps");
    const SynchroRepository = repositoryFactory.get("synchro");
    const state = reactive({
      errorElement: null,
      cast_prop: {},
      // チェックボックス表示用(配列)。cast_propのJSON配列文字列と相互変換する
      selected: {},
      // プロフィール項目(サイトから動的取得。[{key: "profile_{gpi_id}", text: ラベル}])
      profileItems: [],
      profileLoadingFlg: false,
      profileError: false,
      // 読み込み時データ保持(保存前比較用)
      keepData: {
        cast_prop: {},
      },
    });

    // チェックボックス群のマスタ(id→ラベル)。サイトのキャスト編集画面から取得した固定値
    const checkboxGroups = [
      {
        key: "girl_play_detail",
        title: "可能プレイ",
        options: [
          { value: "1", label: "ディープキス" },
          { value: "2", label: "指入れ" },
          { value: "4", label: "素股" },
          { value: "20", label: "全身リップ" },
          { value: "10", label: "生フェラ" },
          { value: "9", label: "玉舐め" },
          { value: "16", label: "69" },
          { value: "19", label: "口内発射" },
          { value: "22", label: "パンティー持ち帰り" },
          { value: "7", label: "ストッキング" },
          { value: "25", label: "アイマスク" },
          { value: "15", label: "オナニー" },
          { value: "18", label: "ローター" },
          { value: "12", label: "バイブ" },
          { value: "8", label: "アナルバイブ" },
          { value: "29", label: "電マ" },
          { value: "27", label: "手コキ" },
          { value: "28", label: "マッサージ" },
          { value: "21", label: "前立線マッサージ" },
          { value: "6", label: "パイズリ" },
          { value: "13", label: "アナル舐め" },
          { value: "5", label: "即尺" },
          { value: "26", label: "顔射" },
          { value: "17", label: "ゴックン" },
          { value: "11", label: "聖水" },
          { value: "24", label: "マット" },
          { value: "14", label: "ＡＦ" },
          { value: "23", label: "３P" },
          { value: "67", label: "アロママッサージ" },
          { value: "68", label: "パウダーマッサージ" },
          { value: "69", label: "乳首舐め" },
        ],
      },
      {
        key: "girl_item_list_2",
        title: "見た目",
        options: [
          { value: "30", label: "人妻系" },
          { value: "98", label: "熟女系" },
          { value: "31", label: "若妻系" },
          { value: "35", label: "ＯＬ系" },
          { value: "36", label: "ギャル系" },
          { value: "37", label: "キャバ嬢系" },
          { value: "39", label: "モデル体型" },
          { value: "40", label: "女子大生系" },
          { value: "41", label: "清楚系" },
          { value: "99", label: "ロリ系" },
          { value: "32", label: "萌え系" },
          { value: "100", label: "黒髪" },
          { value: "101", label: "色白" },
          { value: "102", label: "カワイイ系" },
          { value: "103", label: "キレイ系" },
          { value: "104", label: "美少女系" },
          { value: "105", label: "高身長" },
          { value: "106", label: "小柄" },
          { value: "107", label: "ぽっちゃり" },
          { value: "108", label: "スレンダー" },
        ],
      },
      {
        key: "girl_item_list_8",
        title: "特徴",
        options: [
          { value: "110", label: "素人" },
          { value: "109", label: "AV女優" },
          { value: "114", label: "業界未経験" },
          { value: "121", label: "巨乳" },
          { value: "122", label: "爆乳" },
          { value: "116", label: "美乳" },
          { value: "120", label: "貧乳" },
          { value: "117", label: "美脚" },
          { value: "118", label: "美尻" },
          { value: "119", label: "巨尻" },
          { value: "112", label: "非喫煙" },
          { value: "113", label: "パイパン" },
          { value: "115", label: "タトゥーあり" },
          { value: "111", label: "ニューハーフ" },
        ],
      },
      {
        key: "girl_item_list_7",
        title: "性格",
        options: [
          { value: "84", label: "話し好き" },
          { value: "85", label: "聞き上手" },
          { value: "86", label: "空気を読む" },
          { value: "87", label: "天然" },
          { value: "88", label: "明るい" },
          { value: "89", label: "癒し系" },
          { value: "90", label: "甘えん坊" },
          { value: "91", label: "ツンデレ" },
          { value: "92", label: "おっとり" },
          { value: "93", label: "恥ずかしがり屋" },
          { value: "94", label: "人懐っこい" },
          { value: "95", label: "変態" },
          { value: "96", label: "感度抜群" },
          { value: "97", label: "愛嬌抜群" },
        ],
      },
      {
        key: "girl_item_list_4",
        title: "プレイスタイル",
        options: [
          { value: "123", label: "攻め好き" },
          { value: "63", label: "ドＳ" },
          { value: "124", label: "受身好き" },
          { value: "59", label: "ドＭ" },
          { value: "64", label: "攻め・受身両方OK" },
          { value: "125", label: "イチャイチャ好き" },
          { value: "126", label: "サービス抜群" },
          { value: "127", label: "テクニシャン" },
          { value: "128", label: "濃厚サービス" },
        ],
      },
    ];

    // 文字数制限はサイト準拠で設けない(rules空)
    const items = {
      "grl_comment": {
        text: "コメント",
        rules: "",
        counter: "",
      },
      "grl_shopcomment": {
        text: "お店より一言",
        rules: "",
        counter: "",
      },
    };

    // プロフィール項目（ラベル・並び順・安定ID gpi_id）をサイトから動的取得する
    // キーは profile_{gpi_id}。サイトでプロフィール項目を並び替えても値が項目に正しく追従する
    const getProfileList = async () => {
      state.profileError = false;
      state.profileLoadingFlg = true;
      state.profileItems.splice(0);
      try {
        const response = await retryRequest(
          SynchroRepository.sync_cast_only_hyper_bingo_profile_list,
          {
            shop_id: store.getters["shops/currentShop"].id,
            shop_site_id: props.com_data.id,
          }
        );
        if (response.data) {
          const profile_list = response.data.data.profile_list;
          for (let i = 0; i < profile_list.length; i++) {
            const key = "profile_" + profile_list[i].id;
            state.profileItems.push({ key: key, text: profile_list[i].name });
            // cast_propに未登録なら初期化（取込済みの値があればそのまま使う）
            if (!(key in state.cast_prop)) {
              set(state.cast_prop, key, null);
              state.keepData.cast_prop[key] = null;
            }
          }
        }
      } catch (error) {
        state.profileError = true;
      } finally {
        state.profileLoadingFlg = false;
      }
    };

    // 画面表示用にstate.cast_propの値を整理(媒体コピー処理からreflectCastPropsで呼ばれる)
    const reflectCastProps = async () => {
      // チェックボックスのcast_prop(JSON配列文字列)を表示用配列に変換
      checkboxGroups.forEach((group) => {
        let arr = [];
        const value = state.cast_prop[group.key];
        if (value) {
          try {
            arr = JSON.parse(value);
          } catch (e) {
            arr = [];
          }
        }
        set(state.selected, group.key, Array.isArray(arr) ? arr : []);
      });
    };

    const init = async () => {
      // プロフィール項目を動的取得（ラベル・gpi_idキー）
      getProfileList();

      // cast_propにキーを設定
      for (let key in items) {
        set(state.cast_prop, key, null);
      }
      checkboxGroups.forEach((group) => {
        set(state.cast_prop, group.key, null);
        set(state.selected, group.key, []);
      });
      Object.keys(props.com_data.cast_prop).forEach(function (key) {
        set(state.cast_prop, key, props.com_data.cast_prop[key]);
      });
      // 読み込み時のデータを保持(保存前に比較するため)
      state.keepData.cast_prop = Object.assign({}, state.cast_prop);

      // 画面表示前の後処理
      await reflectCastProps();
    };

    init();

    // チェック処理
    const checkValid = async () => {
      state.errorElement = null;
      return await ctx.refs.observer.validate();
    };

    const setErrorFocus = async () => {
      if (!state.errorElement) return;
      setTimeout(() => {
        state.errorElement.scrollIntoView({
          block: "center",
        });
      }, 300);
    };

    // 保存前にcastPropsの値を整理する(媒体コピー処理からsetCastPropsで呼ばれる)
    const setCastProps = async () => {
      // 表示用配列をcast_prop(JSON配列文字列)に戻す
      checkboxGroups.forEach((group) => {
        set(
          state.cast_prop,
          group.key,
          state.selected[group.key].length === 0
            ? ""
            : JSON.stringify(state.selected[group.key])
        );
      });
    };

    // 保存処理
    const save = async () => {
      // 保存前にcastPropsの値を整理する
      await setCastProps();

      // 更新前と比べて差分があれば更新処理
      if (!utilFunc.compareObject(state.keepData.cast_prop, state.cast_prop)) {
        const prop_param = [];
        for (let key in state.cast_prop) {
          prop_param.push({
            cast_id: props.com_data.cast_id,
            key: props.com_data.prefix + "__" + key,
            value: state.cast_prop[key] ?? "",
          });
        }
        await CastPropsRepository.save_all({ cast_prop: prop_param })
          .then((response) => {
            if (response.data) {
              // データ追加OK
            }
          })
          .catch((error) => {
            throw (
              "ERROR:hyper_bingo.vue/save CastPropsRepository.save_all (" +
              error +
              ")"
            );
          });
      }
      return true;
    };

    return {
      props,
      setting,
      checkboxGroups,
      items,
      ...toRefs(state),
      checkValid,
      setErrorFocus,
      save,
      reflectCastProps,
      setCastProps,
    };
  },
});
</script>

<style scoped></style>
